import { getIn, useFormikContext } from 'formik';
import { useEffect } from 'react';

interface ScrollToErrorProps {
  orderedFields: string[];
  ignoreFields?: string[];
}

const scrollIntoView = (element: HTMLInputElement) => {
  const { y } = element.getBoundingClientRect();

  window.scrollTo({
    top: y + window.scrollY - window.innerHeight / 2,
    behavior: 'smooth',
  });
};

const getTopErrorFieldName = (
  fields: string[],
  errors: Record<string, string>,
  touched: Record<string, boolean>
) =>
  fields.find((name) =>
    Boolean(getIn(errors, name, false) && getIn(touched, name, false))
  ) ?? null;

const getTopErrorField = (
  fields: string[],
  errors: Record<string, string>,
  touched: Record<string, boolean>
) => {
  const fieldName = getTopErrorFieldName(fields, errors, touched);

  if (fieldName) {
    const fieldElementByName = document.querySelector(
      `[name="${fieldName}"]`
    ) as HTMLInputElement;
    const fieldElementById = document.querySelector(
      `#${fieldName}`
    ) as HTMLElement;
    return fieldElementByName || fieldElementById;
  }

  return null;
};

const ScrollToError = ({
  orderedFields,
  ignoreFields = [],
}: ScrollToErrorProps) => {
  const {
    submitCount,
    isValid,
    errors,
    isSubmitting,
    touched,
    setFieldTouched,
  } = useFormikContext();

  useEffect(() => {
    if (isValid || !isSubmitting) {
      return;
    }

    const topErrorField = getTopErrorField(orderedFields, errors, touched);

    if (!topErrorField || ignoreFields.includes(topErrorField.name)) {
      return;
    }

    setFieldTouched(topErrorField.name, true);
    scrollIntoView(topErrorField);
    topErrorField.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount, isValid, isSubmitting, touched]);

  return null;
};

export { ScrollToError };
